// Subscription Migration Utilities
// Convert old subscription structure to new structure with base plans and top-ups
// UPDATED: All Firestore document paths fixed - refresh browser to load new version

import { doc, getDoc, setDoc, deleteDoc, collection, getDocs, query, limit, serverTimestamp } from 'firebase/firestore';
import { calculateNextResetDate, calculateTotalEntitlements } from './subscriptionCalculations';
import { validateSubscriptionStructure } from './subscriptionValidation';
import { createTrialSubscriptionForCompany } from './trialSubscriptionUtils';

/**
 * Migrate a single company subscription to new structure
 * @param {Object} appDb - Firestore database instance
 * @param {string} companyId - Company ID to migrate
 * @returns {Promise<Object>} - Migration result
 */
export const migrateCompanySubscription = async (appDb, companyId) => {
  try {
    console.log(`🔄 Starting migration for company: ${companyId}`);
    
    // Step 1: Try multiple subscription data locations
    let oldSubscription = null;
    let sourceLocation = null;
    
    // 1. Try new structure first: companies/{companyId}/subscription_details/subscription
    const subscriptionRef = doc(appDb, `companies/${companyId}/subscription_details/subscription`);
    const subscriptionSnap = await getDoc(subscriptionRef);
    
    if (subscriptionSnap.exists()) {
      oldSubscription = subscriptionSnap.data();
      sourceLocation = 'companies/subscription_details/subscription';
      
      // Check if already migrated
      if (oldSubscription.basePlan && oldSubscription.topups) {
        console.log(`✅ Company ${companyId} already migrated`);
        return {
          success: true,
          companyId,
          alreadyMigrated: true
        };
      }
    } else {
      // 2. Try legacy structure: company_data/{id}/entitlements
      try {
        const entitlementsRef = doc(appDb, `company_data/${companyId}/entitlements/current`);
        console.log(`🔍 Checking entitlements path: company_data/${companyId}/entitlements/current`);
        const entitlementsSnap = await getDoc(entitlementsRef);
      
        if (entitlementsSnap.exists()) {
          const entitlementsData = entitlementsSnap.data();
          oldSubscription = {
            planId: entitlementsData.plan || 'starter',
            planName: entitlementsData.planName || 'Starter Plan',
            customLimits: entitlementsData.customLimits,
            ...entitlementsData
          };
          sourceLocation = 'company_data/entitlements';
        }
      } catch (error) {
        console.log(`ℹ️ Company ${companyId} has no legacy entitlements: ${error.message}`);
      }
      
      if (!oldSubscription) {
        // 3. Try user-level subscription
        try {
          const userSubRef = doc(appDb, `subscriptions/${companyId}`);
          const userSubSnap = await getDoc(userSubRef);
          
          if (userSubSnap.exists()) {
            const userSubData = userSubSnap.data();
            oldSubscription = {
              planId: userSubData.planType || 'starter',
              planName: userSubData.planName || 'Starter Plan',
              ...userSubData
            };
            sourceLocation = 'subscriptions';
          }
        } catch (error) {
          console.log(`ℹ️ Company ${companyId} has no user subscription: ${error.message}`);
        }
      }
    }
    
    if (!oldSubscription) {
      console.log(`🎁 No subscription found for company: ${companyId} - creating trial subscription`);
      
      // Create trial subscription for companies with no subscription structure
      const trialResult = await createTrialSubscriptionForCompany(appDb, companyId, 'admin');
      
      if (trialResult.success) {
        console.log(`✅ Trial subscription created for company: ${companyId}`);
        return {
          success: true,
          companyId,
          trialCreated: true,
          message: 'Trial subscription created for company with no existing subscription'
        };
      } else {
        return {
          success: false,
          companyId,
          error: `Failed to create trial subscription: ${trialResult.error}`,
          skipped: true
        };
      }
    }
    
    console.log(`📋 Found subscription data from ${sourceLocation}:`, oldSubscription);
    
    // Step 2: Transform to new structure
    const now = new Date();
    const nextResetDate = calculateNextResetDate(now, 0);
    
    const limits = oldSubscription.customLimits || oldSubscription.limits || {
      firebase_operations: 3000,
      storage_mb: 100,
      sales_documents: 3000,
      parties: 3000
    };
    
    const newSubscription = {
      // Base Plan
      basePlan: {
        planId: oldSubscription.planId || 'starter',
        planName: oldSubscription.planName || 'Starter Plan',
        price: oldSubscription.price || 29,
        currency: '₹',
        billing: 'monthly',
        
        startDate: oldSubscription.startDate || now.toISOString(),
        nextResetDate: nextResetDate,
        nextBillingDate: nextResetDate,
        
        purchaseDate: oldSubscription.updated_at || now.toISOString(),
        paidAmount: oldSubscription.price || 29,
        validityDays: 30,
        
        limits: limits,
        
        bonusDays: null,
        
        autoRenew: oldSubscription.autoRenew !== undefined ? oldSubscription.autoRenew : true,
        paymentMethod: oldSubscription.paymentMethod || 'manual',
        status: oldSubscription.status || 'active'
      },
      
      // Top-ups (empty initially)
      topups: [],
      
      // Total Entitlements
      totalEntitlements: {
        firebase_operations: limits.firebase_operations,
        storage_mb: limits.storage_mb,
        sales_documents: limits.sales_documents,
        parties: limits.parties,
        breakdown: {
          fromBasePlan: limits,
          fromTopups: {
            firebase_operations: 0,
            storage_mb: 0,
            sales_documents: 0,
            parties: 0
          }
        }
      },
      
      // Current Month Usage (preserve existing or start fresh)
      currentMonthUsage: {
        period: `${now.toISOString().split('T')[0]} to ${nextResetDate.split('T')[0]}`,
        startDate: now.toISOString(),
        resetDate: nextResetDate,
        
        firebase_operations: 0,
        storage_mb: 0,
        sales_documents: 0,
        parties: 0,
        
        usageBreakdown: {
          fromBasePlan: {
            firebase_operations: 0,
            storage_mb: 0,
            sales_documents: 0,
            parties: 0
          },
          fromTopups: {}
        },
        
        lastUpdated: now.toISOString()
      },
      
      // Upgrade History (preserve existing or create new)
      upgradeHistory: oldSubscription.upgradeHistory || [],
      
      // Scheduled Downgrade
      scheduledDowngrade: null,
      
      // Metadata
      status: oldSubscription.status || 'active',
      created_at: oldSubscription.created_at || now.toISOString(),
      updated_at: now.toISOString(),
      migrated_at: now.toISOString(),
      migrated_from_version: 'v1',
      updated_by: {
        source: 'migration_script',
        adminId: 'system'
      }
    };
    
    // Step 3: Validate new structure
    const validation = validateSubscriptionStructure(newSubscription);
    if (!validation.isValid) {
      console.error(`❌ Validation failed for company ${companyId}:`, validation.errors);
      return {
        success: false,
        companyId,
        error: 'Validation failed',
        validationErrors: validation.errors
      };
    }
    
    // Step 4: Backup old structure
    const backupRef = doc(appDb, `companies/${companyId}/subscription_details/subscription_backup`);
    await setDoc(backupRef, {
      ...oldSubscription,
      backed_up_at: now.toISOString()
    });
    
    // Step 5: Write new structure
    await setDoc(subscriptionRef, newSubscription);
    
    // Migration completed successfully
    
    console.log(`✅ Successfully migrated company: ${companyId}`);
    
    return {
      success: true,
      companyId,
      oldSubscription,
      newSubscription
    };
    
  } catch (error) {
    console.error(`❌ Error migrating company ${companyId}:`, error);
    
    // Migration failed - error logged to console
    
    return {
      success: false,
      companyId,
      error: error.message
    };
  }
};

/**
 * Migrate all companies
 * @param {Object} appDb - Firestore database instance
 * @param {Array} companies - Array of company objects with id property
 * @param {Function} progressCallback - Callback for progress updates
 * @returns {Promise<Object>} - Migration summary
 */
export const migrateAllCompanies = async (appDb, companies, progressCallback = null) => {
  console.log(`🚀 Starting batch migration for ${companies.length} companies`);
  
  const results = {
    total: companies.length,
    successful: 0,
    failed: 0,
    skipped: 0,
    alreadyMigrated: 0,
    errors: [],
    details: []
  };
  
  for (let i = 0; i < companies.length; i++) {
    const company = companies[i];
    const companyId = company.id;
    
    // Progress callback
    if (progressCallback) {
      progressCallback({
        current: i + 1,
        total: companies.length,
        companyId,
        percentage: Math.round(((i + 1) / companies.length) * 100)
      });
    }
    
    const result = await migrateCompanySubscription(appDb, companyId);
    results.details.push(result);
    
    if (result.success) {
      if (result.alreadyMigrated) {
        results.alreadyMigrated++;
      } else {
        results.successful++;
      }
    } else if (result.skipped) {
      results.skipped++;
    } else {
      results.failed++;
      results.errors.push({
        companyId,
        error: result.error,
        validationErrors: result.validationErrors
      });
    }
    
    // Small delay to avoid overwhelming Firestore
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  
  console.log(`✅ Migration complete:`, results);
  
  // Migration summary completed
  
  return results;
};

/**
 * Validate migration for a company
 * @param {Object} appDb - Firestore database instance
 * @param {string} companyId - Company ID to validate
 * @returns {Promise<Object>} - Validation result
 */
export const validateMigration = async (appDb, companyId) => {
  try {
    const subscriptionRef = doc(appDb, `companies/${companyId}/subscription_details/subscription`);
    const subscriptionSnap = await getDoc(subscriptionRef);
    
    if (!subscriptionSnap.exists()) {
      return {
        valid: false,
        companyId,
        error: 'Subscription not found'
      };
    }
    
    const subscription = subscriptionSnap.data();
    const validation = validateSubscriptionStructure(subscription);
    
    return {
      valid: validation.isValid,
      companyId,
      subscription,
      errors: validation.errors
    };
    
  } catch (error) {
    return {
      valid: false,
      companyId,
      error: error.message
    };
  }
};

/**
 * Rollback migration for a company
 * @param {Object} appDb - Firestore database instance
 * @param {string} companyId - Company ID to rollback
 * @returns {Promise<Object>} - Rollback result
 */
export const rollbackMigration = async (appDb, companyId) => {
  try {
    console.log(`🔄 Rolling back migration for company: ${companyId}`);
    
    // Step 1: Get backup
    const backupRef = doc(appDb, `companies/${companyId}/subscription_details/subscription_backup`);
    const backupSnap = await getDoc(backupRef);
    
    if (!backupSnap.exists()) {
      return {
        success: false,
        companyId,
        error: 'No backup found'
      };
    }
    
    const backup = backupSnap.data();
    
    // Remove backup timestamp
    delete backup.backed_up_at;
    
    // Step 2: Restore backup
    const subscriptionRef = doc(appDb, `companies/${companyId}/subscription_details/subscription`);
    await setDoc(subscriptionRef, backup);
    
    // Step 3: Log rollback
    const logRef = doc(collection(appDb, 'migration_logs'));
    await setDoc(logRef, {
      companyId,
      type: 'rollback',
      timestamp: serverTimestamp(),
      status: 'success'
    });
    
    console.log(`✅ Successfully rolled back company: ${companyId}`);
    
    return {
      success: true,
      companyId,
      restoredSubscription: backup
    };
    
  } catch (error) {
    console.error(`❌ Error rolling back company ${companyId}:`, error);
    return {
      success: false,
      companyId,
      error: error.message
    };
  }
};

/**
 * Get migration status for all companies
 * @param {Object} appDb - Firestore database instance
 * @param {Array} companies - Array of company objects
 * @returns {Promise<Object>} - Migration status
 */
export const getMigrationStatus = async (appDb, companies) => {
  const status = {
    total: companies.length,
    migrated: 0,
    notMigrated: 0,
    noSubscription: 0,
    companies: []
  };
  
  for (const company of companies) {
    try {
      let hasSubscription = false;
      let migrationStatus = 'no_subscription';
      let subscriptionData = null;
      
      // Check multiple subscription locations in priority order
      
      // 1. Try new structure first: companies/{companyId}/subscription_details/subscription
      const subscriptionRef = doc(appDb, `companies/${company.id}/subscription_details/subscription`);
      const subscriptionSnap = await getDoc(subscriptionRef);
      
      if (subscriptionSnap.exists()) {
        subscriptionData = subscriptionSnap.data();
        hasSubscription = true;
        
        // Check if already migrated (has basePlan structure)
        if (subscriptionData.basePlan && subscriptionData.topups !== undefined) {
          migrationStatus = 'migrated';
        } else {
          migrationStatus = 'not_migrated';
        }
      } else {
        // 2. Try legacy structure: company_data/{id}/entitlements
        try {
          const entitlementsRef = doc(appDb, `company_data/${company.id}/entitlements/current`);
          console.log(`🔍 Checking entitlements path: company_data/${company.id}/entitlements/current`);
          const entitlementsSnap = await getDoc(entitlementsRef);
        
          if (entitlementsSnap.exists()) {
            subscriptionData = entitlementsSnap.data();
            hasSubscription = true;
            migrationStatus = 'not_migrated';
          }
        } catch (error) {
          console.log(`ℹ️ Company ${company.id} has no legacy entitlements: ${error.message}`);
        }
        
        if (!hasSubscription) {
          // 3. Try user-level subscription
          try {
            const userSubRef = doc(appDb, `subscriptions/${company.id}`);
            const userSubSnap = await getDoc(userSubRef);
            
            if (userSubSnap.exists()) {
              subscriptionData = userSubSnap.data();
              hasSubscription = true;
              migrationStatus = 'not_migrated';
            }
          } catch (error) {
            console.log(`ℹ️ Company ${company.id} has no user subscription: ${error.message}`);
          }
        }
        
        if (!hasSubscription) {
          // 4. Check if this is a trial company (has usage but no subscription)
          try {
            const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
            const usageRef = doc(appDb, `companies/${company.id}/usage/${currentMonth}`);
            const usageSnap = await getDoc(usageRef);
            
            if (usageSnap.exists()) {
              // This is a trial company - has usage data but no formal subscription
              subscriptionData = {
                plan: 'trial',
                planName: 'Trial (Unlimited)',
                isTrial: true,
                usage: usageSnap.data()
              };
              hasSubscription = true;
              migrationStatus = 'trial_needs_migration';
            }
          } catch (error) {
            console.log(`ℹ️ Company ${company.id} has no usage data: ${error.message}`);
          }
        }
      }
      
      // Status counting and company data push INSIDE the try block
      if (!hasSubscription) {
        status.noSubscription++;
      } else if (migrationStatus === 'migrated') {
        status.migrated++;
      } else if (migrationStatus === 'not_migrated') {
        status.notMigrated++;
      } else if (migrationStatus === 'trial_needs_migration') {
        status.notMigrated++; // Count trial companies as needing migration
      }
        
      status.companies.push({
        companyId: company.id,
        companyName: company.name,
        migrationStatus: migrationStatus,
        subscriptionData: subscriptionData ? {
          plan: subscriptionData.basePlan?.planId || subscriptionData.planId || subscriptionData.plan || 'unknown',
          hasBasePlan: !!subscriptionData.basePlan,
          hasTopups: subscriptionData.topups && subscriptionData.topups.length > 0
        } : null
      });
    } catch (error) {
      console.error(`Error checking migration status for ${company.id}:`, error);
      status.companies.push({
        companyId: company.id,
        companyName: company.name,
        migrationStatus: 'error',
        error: error.message
      });
    }
  }
  
  return status;
};

// Migration logs functionality removed - logs are no longer stored

/**
 * Test authentication and permissions
 * @param {Object} appDb - Firestore database instance
 * @returns {Promise<void>}
 */
export const testAuthentication = async (appDb) => {
  try {
    console.log('🔍 Testing authentication and permissions...');
    
    // Get the service user UID from appFirebaseClient
    const { getServiceUserUid } = await import('../appFirebaseClient');
    const serviceUserUid = getServiceUserUid();
    
    // Test 1: Try to read service_readers collection with correct UID
    try {
      if (serviceUserUid) {
        const serviceRef = doc(appDb, 'service_readers', serviceUserUid);
        const serviceSnap = await getDoc(serviceRef);
        if (serviceSnap.exists()) {
          console.log('✅ Can read service_readers collection');
        } else {
          console.log('⚠️ Service readers document does not exist');
        }
      } else {
        console.log('❌ Service user UID not available');
      }
    } catch (serviceError) {
      console.log('❌ Cannot read service_readers collection:', serviceError.message);
    }
    
    // Test 2: Migration logs collection access removed
    
    // Test 3: Try to read a simple collection that should work
    try {
      const usersRef = collection(appDb, 'users');
      const usersSnap = await getDocs(query(usersRef, limit(1)));
      console.log('✅ Can read users collection');
    } catch (usersError) {
      console.log('❌ Cannot read users collection:', usersError.message);
    }
    
  } catch (error) {
    console.log('❌ Authentication test failed:', error.message);
  }
};

// Migration logs initialization removed - no longer needed

export default {
  migrateCompanySubscription,
  migrateAllCompanies,
  validateMigration,
  rollbackMigration,
  getMigrationStatus,
  testAuthentication
};

